/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useState, useEffect, useRef } from "react";
import { LuX } from "react-icons/lu";

const CATEGORIES = [
  { value: "study-academics", label: "Study & Academics" },
  { value: "visa-immigration", label: "Visa & Immigration" },
  { value: "travel-living", label: "Travel & Living" },
  { value: "finance-scholarships", label: "Finance & Scholarships" },
  { value: "career-jobs", label: "Career & Jobs" },
  { value: "social-events", label: "Social & Events" },
];

interface CreateThreadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    body: string | null;
    category: string;
  }) => Promise<void>;
  isSubmitting: boolean;
  // Edit mode
  editMode?: boolean;
  initialData?: {
    title: string;
    body: string | null;
    category: string;
  };
}

export default function CreateThreadModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  editMode = false,
  initialData,
}: CreateThreadModalProps) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("study-academics");
  const [errors, setErrors] = useState<{
    title?: string;
    body?: string;
    category?: string;
    submit?: string;
  }>({});

  const modalRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Focus trap elements
  const firstFocusableRef = useRef<HTMLInputElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editMode && initialData) {
        // Pre-fill with initial data in edit mode
        // eslint-disable-next-line react-hooks/set-state-in-effect -- resets form when modal (re)opens; syncs to isOpen/initialData props
        setTitle(initialData.title);
        setBody(initialData.body || "");
        setCategory(initialData.category);
      } else {
        // Reset form in create mode
        setTitle("");
        setBody("");
        setCategory("study-academics");
      }
      setErrors({});
      // Focus on title input when modal opens
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [isOpen, editMode, initialData]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isSubmitting, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusableRef.current) {
          e.preventDefault();
          lastFocusableRef.current?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusableRef.current) {
          e.preventDefault();
          firstFocusableRef.current?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [isOpen]);

  // Validate form
  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    // Validate title
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      newErrors.title = "Title is required";
    } else if (trimmedTitle.length < 6) {
      newErrors.title = "Title must be at least 6 characters";
    } else if (trimmedTitle.length > 120) {
      newErrors.title = "Title cannot exceed 120 characters";
    }

    // Validate body (optional, but check length if provided)
    const trimmedBody = body.trim();
    if (trimmedBody && trimmedBody.length > 5000) {
      newErrors.body = "Body cannot exceed 5000 characters";
    }

    // Validate category
    if (!category) {
      newErrors.category = "Category is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setErrors({});
      await onSubmit({
        title: title.trim(),
        body: body.trim() || null,
        category,
      });
    } catch (error: any) {
      // Handle API errors
      if (error.response?.status === 400) {
        const errorMessage =
          error.response?.data?.message || "Validation failed";
        if (errorMessage.toLowerCase().includes("title")) {
          setErrors({ title: errorMessage });
        } else if (errorMessage.toLowerCase().includes("body")) {
          setErrors({ body: errorMessage });
        } else if (errorMessage.toLowerCase().includes("category")) {
          setErrors({ category: errorMessage });
        } else {
          setErrors({ submit: errorMessage });
        }
      } else if (error.response?.status === 401) {
        setErrors({ submit: "Please log in to post a thread" });
      } else {
        setErrors({ submit: "Couldn't post. Try again." });
      }
    }
  };

  // Prevent Enter key on title from submitting
  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  if (!isOpen) return null;

  const charCount = body.length;
  const charLimit = 5000;
  const showCharCount = charCount > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 dark:bg-black/60 backdrop-blur-sm"
        onClick={!isSubmitting ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative w-full max-w-2xl max-h-[90dvh] overflow-y-auto rounded-2xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-white/10 p-6 shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 id="modal-title" className="text-xl font-semibold">
            {editMode ? "Edit thread" : "Create a new thread"}
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-white/80 hover:text-gray-900 dark:hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close modal"
            title="Close (ESC)"
          >
            <LuX className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Title Field */}
          <div>
            <label
              htmlFor="thread-title"
              className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-1.5"
            >
              Title <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              ref={(el) => {
                titleInputRef.current = el;
                firstFocusableRef.current = el;
              }}
              id="thread-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={handleTitleKeyDown}
              placeholder="Give your thread a clear, helpful title"
              className={`w-full rounded-lg bg-gray-50 dark:bg-white/5 border ${
                errors.title
                  ? "border-red-400/60 dark:border-red-400/60"
                  : "border-gray-300 dark:border-white/10"
              } px-4 py-2.5 outline-none placeholder-gray-400 dark:placeholder-white/50 focus:border-gray-400 dark:focus:border-white/30 transition`}
              disabled={isSubmitting}
              maxLength={120}
              aria-required="true"
              aria-invalid={!!errors.title}
              aria-describedby={errors.title ? "title-error" : undefined}
            />
            {errors.title && (
              <p
                id="title-error"
                className="mt-1.5 text-sm text-red-500 dark:text-red-400"
                role="alert"
              >
                {errors.title}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-white/50">
              {title.length}/120 characters
            </p>
          </div>

          {/* Body Field */}
          <div>
            <label
              htmlFor="thread-body"
              className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-1.5"
            >
              Body{" "}
              <span className="text-gray-500 dark:text-white/50 font-normal">
                (optional)
              </span>
            </label>
            <textarea
              id="thread-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Share details, context, or tips…"
              className={`w-full h-40 rounded-lg bg-gray-50 dark:bg-white/5 border ${
                errors.body
                  ? "border-red-400/60 dark:border-red-400/60"
                  : "border-gray-300 dark:border-white/10"
              } px-4 py-2.5 outline-none placeholder-gray-400 dark:placeholder-white/50 focus:border-gray-400 dark:focus:border-white/30 transition resize-y`}
              disabled={isSubmitting}
              maxLength={5000}
              aria-invalid={!!errors.body}
              aria-describedby={errors.body ? "body-error" : undefined}
            />
            {errors.body && (
              <p
                id="body-error"
                className="mt-1.5 text-sm text-red-500 dark:text-red-400"
                role="alert"
              >
                {errors.body}
              </p>
            )}
            {showCharCount && (
              <p
                className={`mt-1 text-xs ${
                  charCount > charLimit
                    ? "text-red-500 dark:text-red-400"
                    : "text-gray-500 dark:text-white/50"
                }`}
              >
                {charCount.toLocaleString()}/{charLimit.toLocaleString()}{" "}
                characters
              </p>
            )}
          </div>

          {/* Category Field */}
          <div>
            <label
              htmlFor="thread-category"
              className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-1.5"
            >
              Category <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <select
              id="thread-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={`w-full rounded-lg bg-gray-50 dark:bg-white/5 border ${
                errors.category
                  ? "border-red-400/60 dark:border-red-400/60"
                  : "border-gray-300 dark:border-white/10"
              } px-4 py-2.5 outline-none focus:border-gray-400 dark:focus:border-white/30 transition`}
              disabled={isSubmitting}
              aria-required="true"
              aria-invalid={!!errors.category}
              aria-describedby={errors.category ? "category-error" : undefined}
            >
              {CATEGORIES.map((cat) => (
                <option
                  key={cat.value}
                  value={cat.value}
                  className="bg-white dark:bg-neutral-900"
                >
                  {cat.label}
                </option>
              ))}
            </select>
            {errors.category && (
              <p
                id="category-error"
                className="mt-1.5 text-sm text-red-500 dark:text-red-400"
                role="alert"
              >
                {errors.category}
              </p>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-400/20 px-4 py-3">
              <p
                className="text-sm text-red-600 dark:text-red-400"
                role="alert"
              >
                {errors.submit}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-white/15 hover:bg-gray-100 dark:hover:bg-white/10 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            ref={lastFocusableRef}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2.5 rounded-lg bg-gray-900 dark:bg-white/90 text-white dark:text-black font-medium hover:bg-gray-800 dark:hover:bg-white transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting && (
              <div className="h-4 w-4 border-2 border-white/30 dark:border-black/30 border-t-white dark:border-t-black rounded-full animate-spin" />
            )}
            {editMode ? "Save changes" : "Post thread"}
          </button>
        </div>
      </div>
    </div>
  );
}
